<template>
	<div
		@click="selectApp"
		ref="app_container"
		:class="{ 'on-focus': isFocused }"
		class="app-container width-100 display-flex flex-row flex-align-center flex-justify-space-between"
	>
		<div class="display-flex flex-row flex-align-center">
			<div class="flex-centerize">
				<IntegrationIcon class="field-icon" :integrationName="icon" :size="48" :padding="10" />
			</div>
			<div class="app-details-container flex-justify-center flex-align-start display-flex flex-column">
				<div class="app-title">
					{{ app.service_name }}
				</div>
				<div class="app-description">
					{{ app.description || app.service_name }}
				</div>
			</div>
		</div>
		<Row>
			<ActionCreditsRequiredMenu :action="creditsAction" v-if="showCreditsIcon" class="mr-2" />
			<ActionUpgradeRequiredMenu :workflow="editWorkflow" :action="action" v-if="showUpgradeRequiredIcon" class="mr-2" />
			<div v-if="doesContextualNodeHaveContextualActions" class="contextual-info-container">Contextual actions available</div>
		</Row>
	</div>
</template>

<script>
import { ServiceActionKeys } from '@ply-io/ply-front-shared-utilities';

import { AI_MINI_APP } from '../BlockEditorAppSelect/BlockEditorAppSelect.const';
import ActionCreditsRequiredMenu from '@/components/BlockEditor/IntegrationEditor/ActionCreditsRequiredMenu.vue';
import ActionUpgradeRequiredMenu from '@/components/BlockEditor/IntegrationEditor/ActionUpgradeRequiredMenu.vue';
import { getActionBySlug } from '@/store/modules/actions/getters.js';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters.js';
import { mapGettersShorthand } from '@/store/utils/mappers';
import AppActionService from '@/utils/AppActionService';
import AppService from '@/utils/AppService';

export default {
	name: 'SubMenuItem',
	components: { ActionUpgradeRequiredMenu, ActionCreditsRequiredMenu },
	props: {
		app: { type: Object },
		focusedItemUUID: { type: String },
		doesContextualNodeHaveContextualActions: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ editWorkflow, getActionBySlug }),
		action() {
			return this.getActionBySlug(this.app.action_slug);
		},
		showUpgradeRequiredIcon() {
			return AppActionService.isActionRestricted(this.action);
		},
		showCreditsIcon() {
			return (
				this.app.action_slug == ServiceActionKeys.RunMiniApp && this.app.miniAppType == AI_MINI_APP && !!this.action?.credits_configuration
			);
		},
		creditsAction() {
			return {
				...this.action,
				is_credit_based: !!this.action?.credits_configuration,
				credits_configuration: this.action?.credits_configuration[0][this.app.miniAppType],
			};
		},
		isFocused() {
			const uuid = AppService.getUniqueId(this.app.type, this.app.id, this.app.service_name);
			return this.focusedItemUUID === uuid;
		},
		icon() {
			if (this.app.overrideIcon) {
				return this.app.overrideIcon;
			}
			return this.app.isServiceActionCollection
				? AppService.getServiceActionCollectionIconName(this.app.service_slug)
				: this.app.service_slug;
		},
	},
	methods: {
		selectApp() {
			this.$emit('app-selected', this.app);
		},
		onKeyDown(event) {
			if (this.isFocused) {
				if (event.keyCode === 13) {
					this.selectApp();
				}
			}
		},
		removeOnKeyDownListener() {
			if (this.onKeyDownHandler) {
				window.removeEventListener('keydown', this.onKeyDownHandler, true);
				this.onKeyDownHandler = null;
			}
		},
		listenOnKeyDown() {
			this.removeOnKeyDownListener();
			this.onKeyDownHandler = (e) => this.onKeyDown(e);
			window.addEventListener('keydown', this.onKeyDownHandler, true);
		},
	},
	mounted() {
		this.listenOnKeyDown();
	},
	watch: {
		isFocused: {
			handler(newV) {
				newV && this.$emit('scroll-to-element', this.$refs.app_container.getBoundingClientRect().y - 400);
			},
		},
	},
	data: () => ({ onKeyDownHandler: null }),
	beforeDestroy() {
		this.removeOnKeyDownListener();
	},
};
</script>

<style scoped lang="scss">
$side-padding: 20px;
.on-focus {
	background-color: var(--app-selection-hover-background);
}
.app-container {
	height: 52px;
	margin-top: 6px;
	padding: 0 $side-padding;
	cursor: pointer;
	transition: 0.3s;

	&:hover {
		background-color: var(--app-selection-hover-background);
		transition: 0.2s;
	}
	.app-details-container {
		padding: 0 14px;

		.app-title {
			font-size: 14px;
			color: var(--bold-title);
		}
		.app-description {
			font-size: 11px;
			color: var(--description-text);
		}
	}
}

.contextual-info-container {
	font-size: 14px;
	padding: 2px 4px;
	border-radius: 6px;
	color: var(--white);
	font-weight: normal;
	background: linear-gradient(to right, #5a9ef7, #6493fd, #7786ff, #8f76fe, #aa61f6);
}
</style>
